import type { CheckboxGroupWithTextInputData } from '@ksyos/design-system/dist/components/checkbox-group-with-text-input/create-checkbox-group-options';
import { DateTime } from '@ksyos/date-time';
import { type JSX } from 'react';
import type { CheckboxData } from '@frontend/utils/checkboxGroupUtils';
import { type CustomControlCode, CustomControlMode } from '@common/enums';
import { isGenericKey } from '@common/formEngine/generic/genericFields';
import { PatientInsurance } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsurance';
import { ProcessActivityAssetsDropzone } from '@frontend/components/pages/episodeCategory/customControls/processActivityAssets/ProcessActivityAssetsDropzone';
import {
    BooleanRadioGroupFormGroup,
    CheckboxGroupWithTextInputFormGroup,
    DateInputFormGroup,
    Icd10SelectFormGroup,
    MonthInputFormGroup,
    NestedCheckboxGroupWithTextInputFormGroup,
    NumberInputColumnRowFormGroup,
    QuantityFormGroup,
    RadioGroupWithTextInputFormGroup,
    SelectFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { useProcessActivityContext, useProcessActivityEditContext } from '@frontend/utils';
import {
    convertCheckboxGroupWithTextInputDataToCheckboxData,
    createCheckboxGroupWithTextInputData,
    orderEnumValues,
} from '@frontend/utils/checkboxGroupUtils';
import { createRadioGroupOptions } from '@frontend/utils/radioGroupUtils';
import { useEditFormSectionInputFieldContext } from './useEditFormContext';

export function EditFormSectionInputField<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(): JSX.Element {
    const { serviceCode } = useProcessActivityContext();
    const { data, errors, handleCancel, processActivityContextData } = useProcessActivityEditContext<
        Data,
        TCustomControlCode,
        Version
    >();
    const {
        // Omit `key` from field so that it doesn't spread into other components
        field: { key: fieldKey, ...field },
        isRequired,
        onChange,
    } = useEditFormSectionInputFieldContext<Data, TCustomControlCode, Version>();
    const commonProps = {
        fieldKey,
        isRequired,
        onChange,
    };

    // This is currently a condition for one generic field, but in the future a switch might be more applicable
    if (isGenericKey(fieldKey)) {
        return (
            <PatientInsurance
                isPatientInformed={Boolean(data[fieldKey])}
                mode={CustomControlMode.edit}
                onCancel={handleCancel}
                onChange={({ isPatientInformed }) => onChange(isPatientInformed)}
                serviceCode={serviceCode}
            />
        );
    }

    switch (field.type) {
        case 'assets':
            if ((fieldKey === 'assets' && field.category) || (fieldKey !== 'assets' && !field.category)) {
                throw new Error('Expected assets field to either have key `assets` or have a category defined');
            }

            return (
                <ProcessActivityAssetsDropzone
                    {...field}
                    {...commonProps}
                    assetCategory={field.category}
                    error={errors.get(fieldKey)}
                    fieldKey="assets"
                    hasErrors={errors.has(fieldKey)}
                />
            );
        case 'boolean':
            return (
                <BooleanRadioGroupFormGroup
                    {...field}
                    {...commonProps}
                    isClearable={!isRequired}
                    type="radio"
                />
            );
        case 'checkboxGroup': {
            const options = Array.isArray(field.valueOptions)
                ? field.valueOptions
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: CS-24714 remove any
                  field.valueOptions.fn(processActivityContextData as any);

            if (!options.length) {
                throw new Error('CheckboxGroup group must have value options');
            }

            const orderedEnum = orderEnumValues(field.checkboxEnumType, options);
            const checkboxGroupWithTextInputData = createCheckboxGroupWithTextInputData(
                (data as Record<string, unknown>)[fieldKey] as CheckboxData,
                orderedEnum,
                field.hasTextInputOptions
            );

            return (
                <CheckboxGroupWithTextInputFormGroup
                    {...field}
                    {...commonProps}
                    data={checkboxGroupWithTextInputData}
                    onChange={(value: CheckboxGroupWithTextInputData) =>
                        onChange(convertCheckboxGroupWithTextInputDataToCheckboxData(value))
                    }
                />
            );
        }
        case 'nestedCheckboxGroup': {
            return (
                <NestedCheckboxGroupWithTextInputFormGroup
                    {...field}
                    {...commonProps}
                    data={(data as Record<string, unknown>)[fieldKey] as Record<string, boolean | string>}
                    onChange={(key, value) => onChange({ [key]: value })}
                    options={field.valueOptions}
                />
            );
        }
        case 'dateInput':
            return (
                <DateInputFormGroup
                    {...field}
                    {...commonProps}
                    maxDate={field.maxValue ? DateTime.from(field.maxValue).toString() : undefined}
                />
            );
        case 'icd10':
            return (
                <Icd10SelectFormGroup
                    {...field}
                    {...commonProps}
                />
            );
        case 'monthInput':
            return (
                <MonthInputFormGroup
                    {...field}
                    {...commonProps}
                />
            );
        case 'number':
            return (
                <NumberInputColumnRowFormGroup
                    {...field}
                    {...commonProps}
                    appendedLabel={field.append}
                />
            );
        case 'quantity': {
            return (
                <QuantityFormGroup
                    {...field}
                    {...commonProps}
                    hasErrors={errors.has(`${fieldKey}.value`) || errors.has(`${fieldKey}.unit`)}
                    isClearable={!isRequired}
                    onChange={onChange}
                    unitError={errors.get(`${fieldKey}.unit`)}
                    valueError={errors.get(`${fieldKey}.value`)}
                />
            );
        }
        case 'radioGroup': {
            const options = Array.isArray(field.valueOptions)
                ? field.valueOptions
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: CS-24714 remove any
                  field.valueOptions.fn(processActivityContextData as any);
            if (!options?.length) {
                throw new Error('Radio group must have value options');
            }
            const { layout, ...remainingField } = field;

            return (
                <RadioGroupWithTextInputFormGroup
                    {...remainingField}
                    {...commonProps}
                    onChange={(value: string) => onChange(value || null)}
                    onClearValue={isRequired ? undefined : () => onChange(null)}
                    options={createRadioGroupOptions(
                        options,
                        field.allowOtherOption ? 'Anders, namelijk...' : undefined
                    )}
                    radioGroupLayout={layout}
                />
            );
        }
        case 'select': {
            const options = Array.isArray(field.valueOptions)
                ? field.valueOptions
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: CS-24714 remove any
                  field.valueOptions.fn(processActivityContextData as any);
            if (!options?.length) {
                throw new Error('Select must have value options');
            }

            return (
                <SelectFormGroup
                    placeholder=""
                    {...field}
                    {...commonProps}
                    options={options.map((option) => ({
                        label: option,
                        value: option,
                    }))}
                />
            );
        }
        case 'textArea':
            return (
                <TextAreaFormGroup
                    {...field}
                    {...commonProps}
                />
            );
        case 'textInput': {
            return (
                <TextInputFormGroup
                    {...field}
                    {...commonProps}
                    type="text"
                />
            );
        }
        default:
            throw new Error(`Unknown type: ${field.type}`);
    }
}
